import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { Plus, Trash2, Save, Globe, X, Edit2, Check } from 'lucide-react';

export default function EnvironmentsPanel({ projectId }) {
  const [envs, setEnvs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');

  useEffect(() => { load(); }, [projectId]);

  async function load() {
    try {
      const r = await api.get(`/projects/${projectId}/environments`);
      const envList = r.data.filter(e => e.name !== '__global__');
      setEnvs(envList);
      if (envList.length && !selected) select(envList[0]);
    } catch {}
  }

  function select(env) {
    setSelected(env);
    setEditing(JSON.parse(JSON.stringify(env)));
  }

  function addVar() {
    setEditing(e => ({ ...e, variables: [...(e.variables||[]), { id: Date.now(), key_name:'', value:'', description:'', enabled:true }] }));
  }

  function updVar(idx, k, v) {
    setEditing(e => { const vars=[...e.variables]; vars[idx]={...vars[idx],[k]:v}; return {...e,variables:vars}; });
  }

  function delVar(idx) {
    setEditing(e => ({ ...e, variables: e.variables.filter((_,i)=>i!==idx) }));
  }

  async function save() {
    try {
      await api.put(`/environments/${editing.id}`, { name:editing.name, variables:editing.variables });
      toast.success('Saved!');
      load();
    } catch { toast.error('Failed'); }
  }

  async function create() {
    if (!newName.trim()) return;
    try {
      await api.post(`/projects/${projectId}/environments`, { name:newName, variables:[] });
      setNewName(''); setShowCreate(false); toast.success('Created!'); load();
    } catch { toast.error('Failed'); }
  }

  async function deleteEnv(id, e) {
    e.stopPropagation();
    if (!window.confirm('Delete environment?')) return;
    try {
      await api.delete(`/environments/${id}`);
      toast.success('Deleted');
      if (selected?.id===id) { setSelected(null); setEditing(null); }
      load();
    } catch { toast.error('Failed'); }
  }

  return (
    <div style={{ display:'flex', height:'100%', overflow:'hidden' }}>
      {/* List */}
      <div style={{ width:220, borderRight:'1px solid var(--border)', display:'flex', flexDirection:'column', overflow:'hidden', background:'var(--bg-secondary)' }}>
        <div style={{ padding:'12px 12px 8px', borderBottom:'1px solid var(--border)', flexShrink:0 }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <span style={{ fontSize:14, fontWeight:700 }}>Environments</span>
            <button className="btn-icon" onClick={() => setShowCreate(true)}><Plus size={14}/></button>
          </div>
        </div>
        <div style={{ flex:1, overflow:'auto', padding:'6px' }}>
          {envs.map(env => (
            <div key={env.id} style={{ display:'flex', alignItems:'center', gap:7, padding:'8px 10px', borderRadius:7, cursor:'pointer', marginBottom:1, background: selected?.id===env.id ? 'var(--accent-light)':'transparent', transition:'background .1s' }}
              onClick={() => select(env)}>
              <Globe size={13} style={{ flexShrink:0, color: selected?.id===env.id ? 'var(--accent)':'var(--text-3)' }}/>
              <span style={{ flex:1, fontSize:13, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', color: selected?.id===env.id ? 'var(--accent)':'var(--text-1)', fontWeight: selected?.id===env.id ? 500:400 }}>{env.name}</span>
              {env.is_default && <span style={{ fontSize:9, color:'var(--accent)', background:'var(--accent-light)', padding:'1px 4px', borderRadius:3 }}>Default</span>}
              <button className="btn-icon" style={{ padding:2, opacity:.6 }} onClick={e=>deleteEnv(env.id,e)}><Trash2 size={11} style={{ color:'var(--red)' }}/></button>
            </div>
          ))}
          {!envs.length && <div style={{ padding:'16px 8px', color:'var(--text-3)', fontSize:12, textAlign:'center' }}>No environments</div>}
        </div>
        {showCreate && (
          <div style={{ padding:'8px 10px', borderTop:'1px solid var(--border)', background:'var(--bg)' }}>
            <input placeholder="Environment name" value={newName} onChange={e=>setNewName(e.target.value)} onKeyDown={e=>e.key==='Enter'&&create()} autoFocus style={{ height:28, fontSize:12, marginBottom:5 }}/>
            <div style={{ display:'flex', gap:4 }}>
              <button className="btn btn-primary btn-xs" style={{ flex:1, justifyContent:'center' }} onClick={create}>Add</button>
              <button className="btn btn-secondary btn-xs" onClick={()=>{setShowCreate(false);setNewName('');}}>Cancel</button>
            </div>
          </div>
        )}
      </div>

      {/* Editor */}
      {editing ? (
        <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden' }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 20px', borderBottom:'1px solid var(--border)', flexShrink:0 }}>
            <input style={{ fontSize:16, fontWeight:700, background:'transparent', border:'none', padding:0, fontFamily:'var(--font)', flex:1 }}
              value={editing.name} onChange={e=>setEditing(v=>({...v,name:e.target.value}))} />
            <div style={{ display:'flex', gap:7 }}>
              <button className="btn btn-secondary btn-sm" onClick={addVar}><Plus size={12}/> Add Variable</button>
              <button className="btn btn-primary btn-sm" onClick={save}><Save size={13}/> Save</button>
            </div>
          </div>

          <div style={{ flex:1, overflow:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr style={{ background:'var(--bg-secondary)' }}>
                  <th style={{ width:36, padding:'8px 12px', borderBottom:'1px solid var(--border)', textAlign:'center', fontSize:11, color:'var(--text-3)', fontWeight:500 }}>On</th>
                  <th style={{ padding:'8px 12px', borderBottom:'1px solid var(--border)', textAlign:'left', fontSize:11, color:'var(--text-3)', fontWeight:500 }}>Variable</th>
                  <th style={{ padding:'8px 12px', borderBottom:'1px solid var(--border)', textAlign:'left', fontSize:11, color:'var(--text-3)', fontWeight:500 }}>Value</th>
                  <th style={{ padding:'8px 12px', borderBottom:'1px solid var(--border)', textAlign:'left', fontSize:11, color:'var(--text-3)', fontWeight:500 }}>Description</th>
                  <th style={{ width:36, borderBottom:'1px solid var(--border)' }}></th>
                </tr>
              </thead>
              <tbody>
                {(editing.variables||[]).map((v,i) => (
                  <tr key={i} style={{ borderBottom:'1px solid var(--border)' }}>
                    <td style={{ textAlign:'center', padding:'4px 12px' }}>
                      <input type="checkbox" checked={v.enabled!==false} onChange={e=>updVar(i,'enabled',e.target.checked)} style={{ width:13, height:13 }}/>
                    </td>
                    <td style={{ padding:'4px 8px' }}>
                      <input className="mono" style={{ border:'none', padding:'5px 8px', fontSize:12, fontWeight:500, color: v.enabled!==false?'var(--text-1)':'var(--text-3)' }} placeholder="VARIABLE_NAME" value={v.key_name} onChange={e=>updVar(i,'key_name',e.target.value)}/>
                    </td>
                    <td style={{ padding:'4px 8px' }}>
                      <input style={{ border:'none', padding:'5px 8px', fontSize:12 }} placeholder="value" value={v.value} onChange={e=>updVar(i,'value',e.target.value)}/>
                    </td>
                    <td style={{ padding:'4px 8px' }}>
                      <input style={{ border:'none', padding:'5px 8px', fontSize:12, color:'var(--text-3)' }} placeholder="optional" value={v.description} onChange={e=>updVar(i,'description',e.target.value)}/>
                    </td>
                    <td style={{ padding:'4px 8px', textAlign:'center' }}>
                      <button className="btn-icon" style={{ padding:3 }} onClick={()=>delVar(i)}><X size={13} style={{ color:'var(--red)' }}/></button>
                    </td>
                  </tr>
                ))}
                {!(editing.variables||[]).length && (
                  <tr><td colSpan={5} style={{ padding:'24px', textAlign:'center', color:'var(--text-3)', fontSize:12 }}>
                    No variables. Click "Add Variable" to get started.<br/>
                    <span style={{ fontSize:11, marginTop:4, display:'block' }}>Use <code style={{ fontFamily:'var(--mono)', color:'var(--accent)' }}>{'{{VARIABLE_NAME}}'}</code> in your API URLs and request bodies.</span>
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:10, color:'var(--text-3)' }}>
          <Globe size={40} style={{ opacity:.2 }}/>
          <div style={{ fontSize:13, color:'var(--text-2)' }}>Select an environment to edit</div>
        </div>
      )}
    </div>
  );
}
